import { IntlShape } from 'react-intl';
import { compact, pick, uniqueId } from 'lodash';

import { getFormattedDate } from '@app/utils';
import { itemStatusPanelButtonMessages } from '@app/translations';
import {
  ChargeDto,
  CheckoutDto,
  ITEM_STATUS,
  ItemStatusData,
  STATUS_ICON,
} from '@app/constants';

import { getChargesData } from './getChargesData';
import { getCheckoutsStatusMessage } from './getCheckoutsStatusesData';

interface StatusesData {
  itemId: string;
  intl: IntlShape;
  isNonCatalogItem: boolean;
  locale?: string;
  dueDate?: string;
  isOverdue?: boolean;
  instanceId?: string;
  isVirtualItem?: boolean;
}

const getStatusesData = ({
  itemId,
  intl,
  locale,
  isOverdue = false,
  isNonCatalogItem,
  instanceId,
  dueDate,
  isVirtualItem,
}: StatusesData): ItemStatusData => {
  const status =
    isOverdue || isNonCatalogItem
      ? ITEM_STATUS.overdue
      : ITEM_STATUS.checkedOut;
  const date = dueDate && locale && getFormattedDate({ date: dueDate, locale });

  return {
    itemId,
    icon: STATUS_ICON[status],
    isNonCatalogItem,
    status,
    ...(!isNonCatalogItem && {
      instanceId,
      statusMessage: getCheckoutsStatusMessage({ date, intl }),
      actionButtonText: intl.$t(itemStatusPanelButtonMessages.renew),
    }),
    isVirtualItem,
  };
};

export const getFeesStatusesData = (
  charges: ChargeDto[],
  loans: CheckoutDto[],
  intl: IntlShape,
  locale: string
): ItemStatusData[] =>
  compact(
    charges.map(({ item: chargeItem, chargeAmount, reason }) => {
      const currentLoan = loans.find(
        loan => loan.item?.itemId === chargeItem?.itemId
      );

      return Object.assign(
        getStatusesData(
          currentLoan
            ? {
                ...pick(currentLoan.item, 'itemId', 'instanceId'),
                isOverdue: currentLoan.overdue,
                dueDate: currentLoan.dueDate,
                isNonCatalogItem: !chargeItem,
                intl,
                locale,
                isVirtualItem: chargeItem?.virtual,
              }
            : {
                itemId: uniqueId(reason),
                isNonCatalogItem: !chargeItem,
                intl,
              }
        ),
        getChargesData({
          intl,
          isNonCatalogItem: !chargeItem,
          chargeAmount,
        })
      );
    })
  );
